import { getSettings } from '../../utils/utilities';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { SmartphoneListItemType, SmartphoneType } from '../../types/smartphone/smartphone.type';
import {
  deleteSmartphoneDetails,
  getSmartphoneDetails,
  getSmartphonesList,
  patchSmartphoneDetails,
  postSmartphoneDetails,
  postSmartphoneOwnershipTransfer,
} from './smartphones.action';

const settings = getSettings();

export interface SmartphonesState {
  smartphonesList: null | PaginationType<SmartphoneListItemType>;
  smartphonesListFetchStatus: FETCH_STATUS;
  smartphonesListRouteParams: RouteParamsType;
  smartphone: null | SmartphoneType;
  smartphoneFetchStatus: FETCH_STATUS;
  smartphonePostStatus: FETCH_STATUS;
  smartphonePatchStatus: FETCH_STATUS;
  smartphoneDeleteStatus: FETCH_STATUS;
  smartphoneOwnershipTransferStatus: FETCH_STATUS;
}

export const initialSmartphoneRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-smartphone.id',
  q: '',
};

const INIT_STATE: SmartphonesState = {
  smartphonesListRouteParams: initialSmartphoneRouteParams,
  smartphonesList: null,
  smartphonesListFetchStatus: FETCH_STATUS.NULL,
  smartphone: null,
  smartphoneFetchStatus: FETCH_STATUS.NULL,
  smartphonePostStatus: FETCH_STATUS.NULL,
  smartphonePatchStatus: FETCH_STATUS.NULL,
  smartphoneDeleteStatus: FETCH_STATUS.NULL,
  smartphoneOwnershipTransferStatus: FETCH_STATUS.NULL,
};

const smartphonesSlice = createSlice({
  name: 'smartphones',
  initialState: INIT_STATE,
  reducers: {
    setSmartphonesList: (state, action) => {
      state.smartphonesList = action.payload;
    },
    setSmartphonesListFetchStatus: (state, action) => {
      state.smartphonesListFetchStatus = action.payload;
    },
    setSmartphone: (state, action) => {
      state.smartphone = action.payload;
    },
    setSmartphoneFetchStatus: (state, action) => {
      state.smartphoneFetchStatus = action.payload;
    },
    setSmartphonePostStatus: (state, action) => {
      state.smartphonePostStatus = action.payload;
    },
    setSmartphonePatchStatus: (state, action) => {
      state.smartphonePatchStatus = action.payload;
    },
    setSmartphoneDeleteStatus: (state, action) => {
      state.smartphoneDeleteStatus = action.payload;
    },
    setSmartphoneOwnershipTransferStatus: (state, action) => {
      state.smartphoneOwnershipTransferStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'smartphones') {
        state.smartphonesListRouteParams = { ...state.smartphonesListRouteParams, ...payload.data };
      }
    });

    // GET SMARTPHONES LIST
    builder.addCase(getSmartphonesList.pending, state => {
      state.smartphonesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getSmartphonesList.rejected, (state, action) => {
      if (action.payload) {
        state.smartphonesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getSmartphonesList.fulfilled, (state, action) => {
      state.smartphonesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.smartphonesList = action.payload;
    });

    // GET SMARTPHONE DETAILS
    builder.addCase(getSmartphoneDetails.pending, state => {
      state.smartphoneFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getSmartphoneDetails.rejected, (state, action) => {
      state.smartphoneFetchStatus = FETCH_STATUS.REJECTED;
      state.smartphone = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getSmartphoneDetails.fulfilled, (state, action) => {
      state.smartphoneFetchStatus = FETCH_STATUS.FULFILLED;
      state.smartphone = action.payload;
    });

    // CREATE SMARTPHONE DETAILS
    builder.addCase(postSmartphoneDetails.pending, state => {
      state.smartphonePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postSmartphoneDetails.rejected, (state, action) => {
      state.smartphonePostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postSmartphoneDetails.fulfilled, state => {
      state.smartphonePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Smartphone created successfully');
      });
    });

    // DELETE SMARTPHONE DETAILS
    builder.addCase(deleteSmartphoneDetails.pending, state => {
      state.smartphoneDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteSmartphoneDetails.rejected, (state, action) => {
      state.smartphoneDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteSmartphoneDetails.fulfilled, (state, action) => {
      if (action.payload.fetchList) {
        state.smartphoneDeleteStatus = FETCH_STATUS.NULL;
      } else {
        state.smartphoneDeleteStatus = FETCH_STATUS.FULFILLED;
      }
      setTimeout(() => {
        snackbarUtils.success('Smartphone has been removed!');
      });
    });

    // TRANSFER SMARTPHONE OWNERSHIP
    builder
      .addCase(postSmartphoneOwnershipTransfer.pending, state => {
        state.smartphoneOwnershipTransferStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postSmartphoneOwnershipTransfer.rejected, (state, action) => {
        state.smartphoneOwnershipTransferStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to transfer Smartphone ownership'));
      })
      .addCase(postSmartphoneOwnershipTransfer.fulfilled, state => {
        state.smartphoneOwnershipTransferStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Smartphone ownership transferred successfully!'));
      });

    // PATCH SMARTPHONE DETAILS
    builder.addCase(patchSmartphoneDetails.pending, state => {
      state.smartphonePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchSmartphoneDetails.rejected, (state, action) => {
      state.smartphonePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchSmartphoneDetails.fulfilled, state => {
      state.smartphonePatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Smartphone updated successfully');
      });
    });
  },
});

export const {
  setSmartphonesList,
  setSmartphonesListFetchStatus,
  setSmartphoneDeleteStatus,
  setSmartphonePatchStatus,
  setSmartphonePostStatus,
  setSmartphoneFetchStatus,
  setSmartphone,
  setSmartphoneOwnershipTransferStatus,
} = smartphonesSlice.actions;

export default smartphonesSlice.reducer;
